import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Download, Trash2, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { formatRelativeDate } from '@/lib/format-relative-date';

type ExportStatus = 'pending' | 'processing' | 'done' | 'failed';

interface ExportJob {
  jobId: string;
  status: ExportStatus;
  filters: Record<string, any>;
  rowsDone: number;
  rowsTotal: number | null;
  downloadUrl: string | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

function describeFilters(filters: Record<string, any>): string {
  const parts: string[] = [];
  if (filters.status) parts.push(`status=${filters.status}`);
  if (filters.project) parts.push(`project=${String(filters.project).slice(0, 8)}`);
  if (filters.domain) parts.push(`domain=${filters.domain}`);
  if (filters.search) parts.push(`search=${filters.search}`);
  if (filters.blocklist) parts.push(`blocklist=${filters.blocklist}`);
  if (filters.verifiedFrom || filters.verifiedTo) {
    parts.push(`date=${filters.verifiedFrom || '…'}..${filters.verifiedTo || '…'}`);
  }
  return parts.length ? parts.join(', ') : '—';
}

function StatusBadge({ status }: { status: ExportStatus }) {
  if (status === 'done') return <Badge variant="default" className="bg-emerald-500 hover:bg-emerald-500">Ready</Badge>;
  if (status === 'failed') return <Badge variant="destructive">Failed</Badge>;
  if (status === 'processing') return <Badge variant="secondary">Running</Badge>;
  return <Badge variant="outline">Queued</Badge>;
}

export default function Exports() {
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      const { jobs } = await api.listEmailsExports();
      setJobs(jobs);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 2000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const handleDownload = async (jobId: string) => {
    setDownloadingId(jobId);
    try {
      const blob = await api.downloadEmailsExport(jobId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emails-export-${jobId.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + (err as Error).message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (jobId: string) => {
    if (!confirm('Delete this export? The file will be removed from storage.')) return;
    setDeletingId(jobId);
    try {
      await api.deleteEmailsExport(jobId);
      await fetchJobs();
    } catch (err) {
      alert('Delete failed: ' + (err as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6" />
            Exports
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            CSV export history from Email Explorer. Auto-refreshes.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchJobs}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              No exports yet. Click <span className="font-medium">Export CSV</span> in Email Explorer.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">Created</TableHead>
                  <TableHead>Filters</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[240px]">Progress</TableHead>
                  <TableHead className="w-[200px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const pct = job.rowsTotal && job.rowsTotal > 0
                    ? Math.min(100, Math.round((job.rowsDone / job.rowsTotal) * 100))
                    : 0;
                  return (
                    <TableRow key={job.jobId}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatRelativeDate(job.createdAt)}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground max-w-[360px] truncate">
                        {describeFilters(job.filters)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={job.status} />
                      </TableCell>
                      <TableCell>
                        {job.status === 'failed' ? (
                          <span className="text-xs text-destructive truncate block" title={job.error || ''}>
                            {job.error || 'Unknown error'}
                          </span>
                        ) : (
                          <div>
                            <Progress value={job.status === 'done' ? 100 : pct} className="h-2" />
                            <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                              {job.rowsDone.toLocaleString('en-US')}
                              {job.rowsTotal !== null && ` / ${job.rowsTotal.toLocaleString('en-US')}`}
                              {job.status !== 'done' && job.rowsTotal ? ` (${pct}%)` : ''}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {job.status === 'done' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(job.jobId)}
                              disabled={downloadingId === job.jobId}
                            >
                              {downloadingId === job.jobId ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3 mr-1" />
                              )}
                              Download
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(job.jobId)}
                            disabled={deletingId === job.jobId}
                          >
                            {deletingId === job.jobId ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
